/**
 * F-126 stem helpers — pure, so the two fiddly bits (what a pasted link is
 * allowed to be, and what to call a file nobody titled) are tested rather than
 * discovered in review.
 */
import { BGM_TITLE_MAX_LENGTH, type BgmStem } from "@kola/shared";

import { assetUrl } from "../../core/api.js";

/**
 * The URL an `<audio>` element should load for a stem (any one of a slot's
 * list — a `BgmCellValue` is its first), or null when there is nothing for
 * it to play. An uploaded stem resolves through the asset route (which
 * carries dev identity when it must — `assetUrl`); a linked one plays from
 * wherever the designer pointed it, but ONLY when the link is an audio FILE
 * — see `isDirectAudioUrl`.
 *
 * Most links pasted into a slot are not files at all: a YouTube video, a
 * Spotify track, a Google Drive preview. `<audio>` cannot play a web page, so
 * pointing it at one produced a play button that did nothing but flick back
 * to ▶ — the load failed and there was no way to reach the page it named.
 * Such a stem gets `stemLinkUrl` and an "open" button instead (F-126).
 */
export function stemPlaybackUrl(stem: BgmStem): string | null {
  if (stem.source === "asset") {
    return stem.assetId === undefined ? null : assetUrl(stem.assetId);
  }
  return stem.url !== undefined && isDirectAudioUrl(stem.url) ? stem.url : null;
}

/**
 * The web page a linked stem points at — what the slot OPENS when it cannot
 * play the link itself. Null for an uploaded stem (there is no page) and for
 * a link Kola refuses to hand to the browser at all.
 *
 * Deliberately returned for a direct audio link too: playback can still fail
 * at load time (a private CDN, a 404, a codec this browser lacks), and the
 * chip falls back to opening the link rather than to nothing.
 */
export function stemLinkUrl(stem: BgmStem): string | null {
  if (stem.source !== "url") return null;
  return stem.url !== undefined && isSafeHttpUrl(stem.url) ? stem.url : null;
}

/**
 * Is this a link Kola may point the browser at? http(s) only — anything else
 * a paste can produce (`javascript:`, `data:`, `file:`) is refused, because
 * the value is shared board content: one user's paste would otherwise become
 * every teammate's `<audio src>` or, worse, their click target.
 */
export function isSafeHttpUrl(raw: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    return false;
  }
  return parsed.protocol === "http:" || parsed.protocol === "https:";
}

/**
 * Does this link name an audio FILE, rather than a page about one?
 *
 * By extension, on the path only — the query string carries signatures and
 * cache keys, never the format. It is a heuristic and it is the conservative
 * one: an extensionless direct link (a signed CDN URL) is treated as a page
 * and gets the "open" button, which still reaches the audio. The other way
 * round — guessing "playable" and being wrong — is the silent dead button
 * this replaces.
 */
export function isDirectAudioUrl(raw: string): boolean {
  if (!isSafeHttpUrl(raw)) return false;
  let pathname: string;
  try {
    pathname = new URL(raw).pathname;
  } catch {
    return false;
  }
  try {
    pathname = decodeURIComponent(pathname);
  } catch {
    // A malformed escape is not worth failing over — test the raw path.
  }
  return AUDIO_EXTENSIONS.test(pathname);
}

/**
 * A default title for a pasted link: the file name at the end of the path, or
 * the host when the path has nothing usable ("open.spotify.com" beats
 * "playlist" and beats an empty cell). Percent escapes are decoded so
 * "Main%20Theme.mp3" reads as a person wrote it.
 */
export function titleFromUrl(raw: string): string {
  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    return trimTitle(raw);
  }
  const last = parsed.pathname.split("/").filter(Boolean).pop();
  if (last === undefined || last.length === 0) return trimTitle(parsed.hostname);
  let decoded = last;
  try {
    decoded = decodeURIComponent(last);
  } catch {
    // A malformed escape is not worth failing over — keep the raw segment.
  }
  return trimTitle(stripExtension(decoded) || parsed.hostname);
}

/** A default title for an uploaded file: its name without the extension. */
export function titleFromFileName(name: string): string {
  return trimTitle(stripExtension(name) || name);
}

function stripExtension(name: string): string {
  const dot = name.lastIndexOf(".");
  // `dot > 0` — a leading dot is a hidden file, not an extension.
  return dot > 0 ? name.slice(0, dot) : name;
}

/** Collapse whitespace and clamp to what the cell value accepts. */
export function trimTitle(raw: string): string {
  return raw.replace(/\s+/g, " ").trim().slice(0, BGM_TITLE_MAX_LENGTH);
}

/**
 * The extensions Kola treats as audio — for a dropped FILE (below) and for a
 * pasted LINK (`isDirectAudioUrl`). One list, so a link and a file of the same
 * kind can never disagree about whether the slot can play it.
 */
const AUDIO_EXTENSIONS = /\.(mp3|wav|m4a|aac|ogg|oga|flac)$/i;

/**
 * Is this a file the slot should take from a drag-and-drop? The browser's
 * `File.type` comes from the OS extension map and is blank for anything it
 * does not know, so the extension is the fallback — the server sniffs the
 * bytes regardless and is the real judge.
 */
export function isAudioFile(file: { type: string; name: string }): boolean {
  return file.type.startsWith("audio/") || AUDIO_EXTENSIONS.test(file.name);
}

/**
 * Does a drag carry an audio file? Mid-drag only MIME types are visible, and
 * a blank type is possible (see above), so a blank counts as "maybe" — the
 * drop decides for real with `isAudioFile`.
 */
export function dragHasAudio(event: DragEvent): boolean {
  return [...(event.dataTransfer?.items ?? [])].some(
    (item) => item.kind === "file" && (item.type === "" || item.type.startsWith("audio/")),
  );
}
